import React, { useState } from "react";
import { marketsOptionsPro } from "../constants";
import {
  Box,
  Autocomplete,
  TextField,
  Button,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  getGridNumericOperators,
  getGridStringOperators,
  GridFilterInputValue,
} from "@mui/x-data-grid-pro";
import moment from "moment";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const stringFilterOperators = getGridStringOperators();

export const numberFilterOperators = (() => {
  const operators = getGridNumericOperators().filter(
    (operator) =>
      !(operator.value === "isEmpty" || operator.value === "isNotEmpty")
  );

  const greaterThanOrEqualIndex = operators.findIndex(
    (op) => op.value === ">="
  );

  if (greaterThanOrEqualIndex !== -1) {
    const greaterThanOrEqual = operators.splice(greaterThanOrEqualIndex, 1)[0];
    operators.unshift(greaterThanOrEqual);
  }

  return operators;
})();

function MultiSelectFilter({ item, applyValue, options }) {
  const value = item.value || [];
  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = () => {
    if (selectAll) {
      applyValue({ ...item, value: [] });
    } else {
      applyValue({ ...item, value: options });
    }
    setSelectAll(!selectAll);
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Autocomplete
        sx={{
          width: "350px",
        }}
        multiple
        disableCloseOnSelect
        options={options}
        value={value}
        getOptionLabel={(option) => option}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option}
          </li>
        )}
        onChange={(event, newValue) => {
          applyValue({ ...item, value: newValue });
          setSelectAll(newValue.length === options.length);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {params.InputProps.endAdornment}
                  <Button size="small" onClick={handleSelectAll}>
                    {selectAll ? "Unselect All" : "Select All"}
                  </Button>
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}

function MarketFilter(props) {
  return <MultiSelectFilter {...props} options={marketsOptionsPro} />;
}

export const marketOperators = [
  {
    label: "Market",
    value: "market",
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value || !filterItem.value || !filterItem.operator) {
        return null;
      }

      return (value) => filterItem.value.some((filter) => filter === value);
    },
    InputComponent: MarketFilter,
    InputComponentProps: { type: "string" },
  },
];

function MultiTextFilter({ item, applyValue }) {
  const [inputValue, setInputValue] = useState("");
  const value = item.value || [];

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const handleChange = (event, newValue) => {
    applyValue({ ...item, value: newValue });
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={value}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      renderInput={(params) => (
        <TextField {...params} placeholder="Type and press enter" />
      )}
      sx={{ width: "350px" }}
    />
  );
}

export const simplifiedStringFilterOperators = [
  {
    value: "contains",
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value || filterItem.value.length === 0) {
        return null;
      }
      return (value) => {
        if (!value) return false;
        const lowercaseValue = value.toLowerCase();
        return filterItem.value.some((item) =>
          lowercaseValue.includes(item.toLowerCase())
        );
      };
    },
    InputComponent: MultiTextFilter,
    InputComponentProps: { type: "text" },
    label: "contains",
  },

  {
    value: "doesNotContain",
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value || filterItem.value.length === 0) {
        return null;
      }
      return (value) => {
        if (!value) return false;
        const lowercaseValue = value.toLowerCase();
        return !filterItem.value.some((item) =>
          lowercaseValue.includes(item.toLowerCase())
        );
      };
    },
    InputComponent: MultiTextFilter,
    InputComponentProps: { type: "text" },
    label: "does not contain",
  },
  stringFilterOperators.find((op) => op.value === "equals"),
  stringFilterOperators.find((op) => op.value === "isAnyOf"),
];

export const rateFilterOperators = [
  ...numberFilterOperators,
  {
    value: "isMaxOdds",
    getApplyFilterFn: () => {
      return (value, row) => {
        return row.rate === row.maxOdds;
      };
    },
    InputComponent: null,
    label: "are highest odds",
  },
];

export const dateFilterOperators = [
  {
    label: "Next 24 hours",
    value: "next24",
    getApplyFilterFn: (filterItem) => {
      return (value) => {
        if (!value) return false;
        const now = moment();
        const eventTime = moment(value);
        return eventTime.isBetween(now, moment().add(24, "hours"));
      };
    },
    InputComponent: ({ item, applyValue }) => {
      return null; // No input needed for this operator
    },
  },
  {
    label: "Next 48 hours",
    value: "next48",
    getApplyFilterFn: (filterItem) => {
      return (value) => {
        if (!value) return false;
        const now = moment();
        const eventTime = moment(value);
        return eventTime.isBetween(now, moment().add(48, "hours"));
      };
    },
    InputComponent: ({ item, applyValue }) => {
      return null;
    },
  },
  {
    label: "Today",
    value: "today",
    getApplyFilterFn: (filterItem) => {
      return (value) => {
        if (!value) return false;
        return moment(value).isSame(moment(), "day");
      };
    },
    InputComponent: ({ item, applyValue }) => {
      return null;
    },
  },
  {
    label: "Tomorrow",
    value: "tomorrow",
    getApplyFilterFn: (filterItem) => {
      return (value) => {
        if (!value) return false;
        return moment(value).isSame(moment().add(1, "day"), "day");
      };
    },
    InputComponent: ({ item, applyValue }) => {
      return null;
    },
  },
  {
    label: "This weekend",
    value: "weekend",
    getApplyFilterFn: (filterItem) => {
      return (value) => {
        if (!value) return false;
        const eventTime = moment(value);
        const saturday = moment().day(6).startOf("day");
        const sunday = moment().day(7).endOf("day");
        return eventTime.isBetween(saturday, sunday, undefined, "[]");
      };
    },
    InputComponent: ({ item, applyValue }) => {
      return null;
    },
  },
  {
    label: "Custom range",
    value: "customRange",
    getApplyFilterFn: (filterItem) => {
      return (value) => {
        if (!value || !filterItem.value) return false;
        const { startDate, endDate, timeRange } = filterItem.value;
        if (!startDate || !endDate) return false;

        const eventTime = moment(value);
        const start = moment(startDate);
        const end = moment(endDate).endOf("day");

        // Apply time range if specified
        if (timeRange) {
          const [startHour, endHour] = timeRange.split("-").map(Number);
          const eventHour = eventTime.hour();
          if (eventHour < startHour || eventHour >= endHour) return false;
        }

        return eventTime.isBetween(start, end, undefined, "[]");
      };
    },
    InputComponent: ({ item, applyValue }) => {
      const [startDate, setStartDate] = useState(item.value?.startDate || "");
      const [endDate, setEndDate] = useState(item.value?.endDate || "");
      const [timeRange, setTimeRange] = useState(item.value?.timeRange || "");

      const handleApply = () => {
        applyValue({ ...item, value: { startDate, endDate, timeRange } });
      };

      const timeRanges = [
        { label: "All day", value: "" },
        { label: "Morning (6am-12pm)", value: "6-12" },
        { label: "Afternoon (12pm-6pm)", value: "12-18" },
        { label: "Evening (6pm-12am)", value: "18-24" },
        { label: "Night (12am-6am)", value: "0-6" },
      ];

      return (
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Time of Day</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              label="Time of Day"
            >
              {timeRanges.map((range) => (
                <MenuItem key={range.value} value={range.value}>
                  {range.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button onClick={handleApply} variant="contained">
            Apply Filter
          </Button>
        </Box>
      );
    },
  },
  {
    label: "After",
    value: "after",
    getApplyFilterFn: (filterItem) => {
      return (value) => {
        if (!value || !filterItem.value) return false;
        return moment(value).isAfter(moment(filterItem.value));
      };
    },
    InputComponent: ({ item, applyValue }) => {
      return (
        <TextField
          type="datetime-local"
          value={item.value || ""}
          onChange={(e) => applyValue({ ...item, value: e.target.value })}
          sx={{ width: 250, mt: 1 }}
          InputLabelProps={{ shrink: true }}
        />
      );
    },
  },
  {
    label: "Before",
    value: "before",
    getApplyFilterFn: (filterItem) => {
      return (value) => {
        if (!value || !filterItem.value) return false;
        return moment(value).isBefore(moment(filterItem.value));
      };
    },
    InputComponent: ({ item, applyValue }) => {
      return (
        <TextField
          type="datetime-local"
          value={item.value || ""}
          onChange={(e) => applyValue({ ...item, value: e.target.value })}
          sx={{ width: 250, mt: 1 }}
          InputLabelProps={{ shrink: true }}
        />
      );
    },
  },
];
